import React, { createContext, useContext } from "react";
import { fetchUserAttributes, fetchAuthSession } from "aws-amplify/auth";

export type UserType = {
  [key: string]: any;
} | null;

export const UserContext = createContext<UserType>(null);

export const useUser = () => useContext(UserContext);

// Cognito のユーザ属性を取得する const（非同期関数）
export type CognitoAttributes = Record<string, string | undefined>;

export type CognitoAuthSnapshot = {
  attributes: CognitoAttributes;
  email: string;
  groups: string[];
  idToken?: string;
  accessToken?: string;
};

const COGNITO_AUTH_SNAPSHOT_TTL_MS = 5000;
let cognitoAuthSnapshotCache:
  | { value: CognitoAuthSnapshot; createdAt: number }
  | null = null;
let cognitoAuthSnapshotPromise: Promise<CognitoAuthSnapshot> | null = null;

const normalizeCognitoGroups = (claim: unknown): string[] => {
  if (Array.isArray(claim)) return claim.map((g) => String(g));
  if (typeof claim === 'string' && claim.trim()) return [claim.trim()];
  return [];
};

export const getCognitoAuthSnapshot = async (
  options?: { force?: boolean },
): Promise<CognitoAuthSnapshot> => {
  const now = Date.now();
  if (
    !options?.force &&
    cognitoAuthSnapshotCache &&
    now - cognitoAuthSnapshotCache.createdAt < COGNITO_AUTH_SNAPSHOT_TTL_MS
  ) {
    return cognitoAuthSnapshotCache.value;
  }

  if (!options?.force && cognitoAuthSnapshotPromise) {
    return cognitoAuthSnapshotPromise;
  }

  cognitoAuthSnapshotPromise = (async () => {
    const [attrsResult, sessionResult] = await Promise.allSettled([
      fetchUserAttributes(),
      fetchAuthSession(),
    ]);

    const attributes: CognitoAttributes =
      attrsResult.status === 'fulfilled' ? attrsResult.value : {};
    if (attrsResult.status === 'rejected') {
      console.error('Failed to fetch Cognito user attributes', attrsResult.reason);
    }

    const tokens =
      sessionResult.status === 'fulfilled' ? sessionResult.value.tokens : undefined;
    if (sessionResult.status === 'rejected') {
      console.error('Failed to fetch Cognito auth session', sessionResult.reason);
    }

    const idPayload: any = tokens?.idToken?.payload;
    const accessPayload: any = tokens?.accessToken?.payload;
    const groups = normalizeCognitoGroups(
      idPayload?.['cognito:groups'] ?? accessPayload?.['cognito:groups'],
    );

    const value: CognitoAuthSnapshot = {
      attributes,
      email: String(attributes.email ?? '').trim(),
      groups,
      idToken: tokens?.idToken?.toString(),
      accessToken: tokens?.accessToken?.toString(),
    };
    cognitoAuthSnapshotCache = { value, createdAt: Date.now() };
    // console.log('[Cognito][DEBUG temporary] auth snapshot', value); // TODO: 確認後に削除
    return value;
  })();

  try {
    return await cognitoAuthSnapshotPromise;
  } finally {
    cognitoAuthSnapshotPromise = null;
  }
};

/**
 * Cognitoのユーザ属性を取得する
 * 失敗時は空オブジェクトを返す
 */
export const getCognitoUserAttributes = async (): Promise<CognitoAttributes> => {
  try {
    const snapshot = await getCognitoAuthSnapshot();
    return snapshot.attributes;
  } catch (error) {
    console.error("Failed to fetch Cognito user attributes", error);
    return {};
  }
};


// Cognito のユーザ名（name / preferred_username / nickname の順に優先）を返す
export const getCognitoUserName = async (): Promise<string> => {
  try {
    const attrs = (await getCognitoAuthSnapshot()).attributes;
    const name =
      (attrs['name'] ?? '') ||
      (attrs['preferred_username'] ?? '') ||
      (attrs['nickname'] ?? '');
    return (name ?? '').trim();
  } catch (error) {
    console.error('Failed to fetch Cognito user name', error);
    return '';
  }
};

// Cognito のメールアドレスを返す
export const getCognitoUserEmail = async (): Promise<string> => {
  try {
    const snapshot = await getCognitoAuthSnapshot();
    return snapshot.email;
  } catch (error) {
    console.error('Failed to fetch Cognito user email', error);
    return '';
  }
};

/**
 * Cognitoのグループ一覧を返す（IDトークン/アクセストークンの 'cognito:groups' を参照）
 * 注意: adminListGroupsForUser はクライアントから直接呼ぶべきではないため、まずはトークンのクレームから取得します。
 * 必要に応じてバックエンド経由で Admin API を呼ぶ構成に拡張してください。
 */
export const getCognitoUserGroups = async (): Promise<string[]> => {
  try {
    const snapshot = await getCognitoAuthSnapshot();
    return snapshot.groups;
  } catch (error) {
    console.error('Failed to fetch Cognito user groups', error);
    return [];
  }
};
